import type { InterpolatedFramePatch, InterpolatedTransitionPhase, SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toCameraViewId } from '@/workflow-runner/_pure/derivation/cameraGraph';
import type { WorkflowCameraDirectionEffect } from '@/workflow-runner/_contract/_~generated/cameraDirectionEffect';

function isInterpolated(phase: TransitionPhase): phase is InterpolatedTransitionPhase {
    return phase.transitionOptions.transitionKind === 'interpolated';
}

/** Drift throughout inspection, more slowly during deliberate reading/processing holds. */
export function deriveAutoplayCameraGlide(args: {
    frame: SpaceDerivationFrame;
    phases: TransitionPhase[];
    stepKey: string;
    resourceType: string;
    policy: Extract<WorkflowCameraDirectionEffect, { cue: 'autoplay-glide' }>;
}): TransitionPhase[] {
    const view = toCameraViewId(args.stepKey, args.resourceType, 'glide');
    const fromView = toCameraViewId(args.stepKey, args.resourceType, 'folded');
    if (!args.frame.spaceRenderOptions?.camera?.graph?.views[view]) return args.phases;
    const duration = (phase: TransitionPhase) => phase.transitionOptions.transitionKind === 'interpolated'
        ? phase.transitionOptions.duration : phase.transitionOptions.delay ?? 0;
    const weight = (phase: TransitionPhase) => /processing-hold|post-output-hold/.test(phase.transitionOptions.transitionKey ?? '')
        ? args.policy.holdWeight : 1;
    const total = args.phases.reduce((sum, phase) => sum + duration(phase) * weight(phase), 0);
    if (total === 0) return args.phases;
    let elapsed = 0;
    return args.phases.map((phase) => {
        const milliseconds = duration(phase);
        if (milliseconds === 0) return phase;
        if (phase.cameraNavigation) throw new Error('Same-type autoplay glide cannot replace an existing camera journey.');
        if (phase.transitionOptions.transitionKind === 'instant' && Object.keys(phase.patch).length > 0) {
            throw new Error('Autoplay camera glide requires hold patches to be empty.');
        }
        elapsed += milliseconds * weight(phase);
        const patch: InterpolatedFramePatch = isInterpolated(phase) ? phase.patch : {};
        return {
            patch,
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: phase.transitionOptions.transitionKey,
                duration: milliseconds,
                easing: args.policy.easing,
            },
            cameraNavigation: { view, blend: { fromView, fraction: Math.min(1, elapsed / total) } },
        };
    });
}
